import React, { useEffect, useState } from "react";
import { Download, Loader2, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EraViewModal from "@/components/EraViewModal";
import { getEobAnalysis, getEra } from "@/lib/eobEraV2";

// Right slide-over that shows the EOB/ERA remittance without leaving the list —
// the "peek & return" flow billers actually use. Fetches on open.
export function EraDrawer({ open, check, onClose }) {
  const navigate = useNavigate();
  const fileId = check?.id;
  const filename = check?.filename || "Remittance";

  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [era, setEra] = useState({ available: false, text: "" });
  const [error, setError] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !fileId) return;
    let alive = true;
    setLoading(true); setError(false); setShowRaw(false); setAnalysis(null); setEra({ available: false, text: "" });
    (async () => {
      const [an, er] = await Promise.all([
        getEobAnalysis(fileId).catch(() => null),
        getEra(fileId).catch(() => ({ available: false, text: "" })),
      ]);
      if (!alive) return;
      setAnalysis(an);
      setEra(er || { available: false, text: "" });
      setError(!an);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, fileId]);

  if (!open) return null;

  const download835 = () => {
    if (!era?.text) return;
    const blob = new Blob([era.text], { type: "application/edi-x12" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(filename || "eob").replace(/\.[^.]+$/, "")}.era`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="pp-sp-backdrop" onClick={onClose} data-testid="pp-erad-backdrop" />
      <aside className="pp-sp pp-erad" role="dialog" aria-label="View ERA" data-testid="pp-era-drawer">
        <div className="pp-sp-head">
          <div className="min-w-0">
            <div className="pp-t">View ERA</div>
            <div className="pp-sp-hint" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{filename}</div>
          </div>
          <div className="pp-erad-tools">
            {analysis && !loading && !error ? (
              <>
                <button className="pp-pbtn pp-manage" onClick={download835} disabled={!era.text} data-testid="pp-erad-download">
                  <Download size={12} style={{ marginRight: 5 }} /> Download 835
                </button>
                {era.text ? (
                  <button className="pp-pbtn pp-history" onClick={() => setShowRaw((v) => !v)} data-testid="pp-erad-raw">
                    {showRaw ? "Hide 835" : "Raw 835"}
                  </button>
                ) : null}
              </>
            ) : null}
            <button className="pp-ovf" title="Open full page" data-testid="pp-erad-fullpage"
              onClick={() => navigate(`/payments/era/${fileId}`, { state: { filename } })}>
              <ExternalLink size={14} />
            </button>
            <button className="pp-sp-close" onClick={onClose} data-testid="pp-erad-close" aria-label="Close"><X size={16} /></button>
          </div>
        </div>

        <div className="pp-erad-body" data-testid="pp-erad-body">
          {loading ? (
            <div className="pp-erad-center">
              <Loader2 size={24} className="animate-spin" style={{ color: "var(--ink)" }} />
              <div style={{ fontSize: 12, color: "var(--sub)" }}>Loading remittance…</div>
            </div>
          ) : error ? (
            <div className="pp-erad-center">
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Couldn't load this remittance</div>
              <div style={{ fontSize: 11, color: "var(--sub)" }}>The analysis for this file isn't available yet.</div>
            </div>
          ) : (
            <EraViewModal
              variant="records"
              hideToolbar
              showRaw={showRaw}
              onToggleRaw={() => setShowRaw((v) => !v)}
              analysis={analysis}
              eraText={era.text}
              filename={filename}
              originalFile={null}
              onDownload={download835}
              onChooseFlow={onClose}
              onFinish={onClose}
            />
          )}
        </div>
      </aside>
    </>
  );
}
